import { makeStyles } from "tss-react/mui"
import { alpha } from "@mui/material"
import {
  blueGrey,
  indigo,
  lightBlue,
  orange,
  teal
} from "../../../../mp-core/themes/palette"

export type GhostieType =
  | "whats_new"
  | "ready"
  | "tip"
  | "general"
  | "custom"
  | "feedback_request"

interface StylesProps {
  isCardOpen: boolean
  isCardHidden: boolean
  type?: GhostieType
}

export type StylesType = Record<
  | "cardTitle"
  | "image"
  | "divider"
  | "cardContainer"
  | "openCloseButton"
  | "cardColorHead"
  | "cardContent"
  | "cardTypeChip"
  | "cardDescription"
  | "cardActions"
  | "paginationContainer"
  | "ratingScale"
  | "feedbackInput",
  string
>

const CARD_COLORS = {
  whats_new: {
    mainColor: indigo["A400"],
    lightColor: indigo["A200"]
  },
  ready: {
    mainColor: orange["A700"],
    lightColor: orange["A400"]
  },
  tip: {
    mainColor: orange["A700"],
    lightColor: orange["A400"]
  },
  general: {
    mainColor: teal["A700"],
    lightColor: teal["A400"]
  },
  custom: {
    mainColor: teal["A700"],
    lightColor: teal["A400"]
  },
  feedback_request: {
    mainColor: teal["A700"],
    lightColor: teal["A400"]
  }
}

export const useStyles = makeStyles<StylesProps>()(
  (
    { spacing, breakpoints, palette },
    { isCardOpen, isCardHidden: _isCardHidden, type = "general" }
  ) => {
    const cardColors = CARD_COLORS[type] ?? {
      mainColor: orange["A700"],
      lightColor: orange["A400"]
    }

    return {
      cardContainer: {
        bottom: spacing(0),
        width: "52px",
        height: "60px",
        marginRight: spacing(4),
        marginBlockStart: "auto",
        transform: "translateY(calc(100% - 60px))",
        transition:
          "transform 0.25s ease-out, width 0.25s ease-out, height 0.25s ease-out, box-shadow 0.25s ease-out",

        ":last-child": {
          marginRight: 0
        },

        [breakpoints.down("sm")]: {
          width: "40px",
          height: "45px",
          transform: "translateY(calc(100% - 45px))"
        },

        ...(isCardOpen
          ? {
              width: "315px",
              height: "100%",
              transform: "translateY(0px)",
              transition: "transform 0.25s ease-out, box-shadow 0.25s ease-out",
              boxShadow: "1px 45px 10px 0px #9e9e9e7a",

              [breakpoints.down("sm")]: {
                width: "315px",
                height: "100%",
                transform: "translateY(0px)"
              }
            }
          : {})
      },
      cardContentContainer: {
        opacity: 0,
        transition: "opacity 0.25s ease-out",
        backgroundColor: palette.common.white,

        ...(isCardOpen
          ? {
              opacity: 1,
              transition: "opacity 0.25s ease-out",

              [breakpoints.down("sm")]: {
                transition: "transform 0.25s ease-out"
              }
            }
          : {})
      },
      openCloseButton: {
        width: "52px",
        height: "60px",
        backgroundColor: `${cardColors.mainColor}`,
        color: `${palette.common.white}`,
        borderRadius: "50px 50px 0px 0px",
        outline: "none",
        boxShadow: "none !important",
        transition: "all 0.25s ease-out",

        "&:hover": {
          backgroundColor: cardColors.lightColor
        },
        "&:focus, &:focus-visible": {
          border: `1px solid ${lightBlue[300]}`,
          outline: `3px solid ${alpha(lightBlue[200], 0.2)}`
        },

        "& .material-icons": {
          fontSize: "1.125rem"
        },

        ...(isCardOpen && {
          height: "50px",
          marginBottom: "-10px"
        }),

        [breakpoints.down("sm")]: {
          width: "40px",
          height: "45px",

          "& .material-icons": {
            fontSize: "0.875rem"
          }
        }
      },
      cardColorHead: {
        height: "10px",
        width: "100%",
        backgroundColor: cardColors.mainColor
      },
      image: {
        width: "315px",
        height: "100px",
        overflow: "hidden",
        objectFit: "cover"
      },
      cardContent: {
        padding: spacing(5, 5, 3)
      },
      cardTypeChip: {
        marginBottom: spacing(3),
        color: palette.common.white,
        backgroundColor: cardColors.mainColor
      },
      cardTitle: {
        marginBottom: spacing(4)
      },
      cardDescription: {
        marginBottom: spacing(3),
        maxHeight: "100px",
        height: "100%",
        overflow: "auto"
      },
      cardActions: {
        display: "flex",
        justifyContent: "flex-end",
        alignItems: "center"
      },
      divider: { backgroundColor: blueGrey[100] },
      paginationContainer: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: spacing(2, 0),
        backgroundColor: palette.common.white
      },
      ratingScale: {
        marginBottom: spacing(3)
      },
      feedbackInput: {
        "& .MuiFilledInput-root": {
          "& .MuiFilledInput-input": {
            padding: spacing(1, 3)
          }
        },
        marginBottom: spacing(4)
      }
    }
  }
)
